import { DestroyRef, inject, Injectable, Injector } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { AngularFireFunctions } from "@angular/fire/compat/functions";
import { doc, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { ActivatedRoute, Data, NavigationEnd, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { LoadingService } from "@core/loading.service";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import * as CryptoJS from "crypto-js";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BehaviorSubject, defer, from, Observable, of, throwError } from "rxjs";
import { catchError, delay, filter, map, retryWhen, scan, shareReplay, switchMap, take } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { VERSION } from "../../environments/version";
import { CommonVariable } from "../app.constants";
import { CommonService } from "./common/common.service";
import { ClarityService } from "./clarity.service";
import { MixpanelService } from "./mixpanel.service";
import { PosthogService } from "./posthog.service";
import {
  buildPaymentSucceededProps,
  firstPresentString,
  firstPurchaseAmount,
  mergePaymentPurchaseContext,
  PaymentPurchaseContext,
  resolvePaymentAmountAndOffer,
} from "./posthog-payment-props.helper";
import { SafeLocalStorageService } from "./safe-local-storage.service";
declare let fbq: any;
declare let dataLayer;
declare let analytics;
declare var window;

// LOTTOSOCIAL-PWA-1NB6: single source of truth for the partial-registration route segment.
export const GTM_PARTIAL_REG_ROUTE = 'partial-registration';

/**
 * Top-level routes where GTM Axon custom HTML tags read localStorage.jackpotPrize.prize
 * and therefore need a safe seed (LOTTOSOCIAL-PWA-1NC4: direct /free-reg-step4c loads).
 * The app is served at the domain root (`<base href="/">` in src/index.html), so these
 * routes are always the first path segment of both router.url and location.pathname.
 */
export const GTM_PRIZE_SEED_TOP_LEVEL_ROUTES: readonly string[] = Object.freeze([
  'free-reg-step4c',
  'free-reg-complete-profile',
  'account-created',
]);

/**
 * Pure helper: true when `url` (an Angular router URL or a location.pathname) resolves
 * to a route where GTM Axon tags need a safe jackpotPrize seed. partial-registration
 * matches as any path segment (existing LOTTOSOCIAL-PWA-1NB6 behaviour);
 * GTM_PRIZE_SEED_TOP_LEVEL_ROUTES must be the first segment (app served at base href
 * "/"). Query, hash and matrix params are ignored. If an absolute URL is passed, its
 * scheme and host are intentionally ignored (only the path is inspected) — callers
 * only pass the app's own router URL / pathname, never user-controlled URLs.
 * The app uses path-based routing only, so hash routes (`/#/x`) are not considered.
 */
export function isGtmPrizeSeedPath(url: string | null | undefined): boolean {
  // Drop scheme + authority if present (e.g. https://host/free-reg-step4c -> /free-reg-step4c).
  const withoutOrigin = (url ?? '').replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
  const path = withoutOrigin.split('?')[0].split('#')[0];
  const segments = path.split('/').filter(Boolean).map(s => s.split(';')[0]);
  if (segments.includes(GTM_PARTIAL_REG_ROUTE)) {
    return true;
  }
  return GTM_PRIZE_SEED_TOP_LEVEL_ROUTES.includes(segments[0] ?? '');
}

/** Lotto Social Hope Ltd Meta Pixel ÔÇö scoped to ProSub 4793 / 4794 only (HATDEV-100288). */
const HOPE_META_PIXEL_ID = "3709858235976248";
const HOPE_PROSUB_IDS = new Set(["4793", "4794"]);

@Injectable({ providedIn: "root" })
export class GTMSegmentService {
  private hopePixelInitialized = false;
    purchaseTagDetails$ = new BehaviorSubject<any>({});
  userIsNew: boolean = false;
  prosubDetails: any;
  prosub: any;
  // LOTTOSOCIAL-PWA-1NB6: single source of truth for the partial-registration route segment.
  // Used by isPartialRegUrl() — never duplicate this string elsewhere.
  private readonly PARTIAL_REG_ROUTE = GTM_PARTIAL_REG_ROUTE;
  // True once the safe prize:0 defaults have been pushed for the current partial-registration
  // visit. Reset via NavigationEnd in the constructor when the user successfully leaves the route.
  private partialRegDefaultsPushed = false;
  private readonly destroyRef = inject(DestroyRef);
  userProduct$ = new BehaviorSubject<any>({});
  vc: string;
  queryParamsToNavigate: any;
  translatedToastMsg: any;
  selectedLanguage: string = "en";
  public TP1: string;
  public TP2: string;
  public TP3: string;
  public T_ID: string;
  constructor(
    private router: Router,
    private analyticsFire: AngularFireAnalytics,
    private actRoute: ActivatedRoute,
    private authService: AuthService,
    private firestore: Firestore,
    private angularFireFunctions: AngularFireFunctions,
    private loading: LoadingService,
    private injector: Injector,
    private commonService: CommonService,
    private safeLocalStorage: SafeLocalStorageService,
    private clarityService: ClarityService,
    private mixpanelService: MixpanelService,
    private posthogService: PosthogService
  ) {
    // Safely initialize tracking parameters using SafeLocalStorageService
    this.TP1 = this.safeLocalStorage.getItem("TP1");
    this.TP2 = this.safeLocalStorage.getItem("TP2");
    this.TP3 = this.safeLocalStorage.getItem("TP3");
    this.T_ID = this.safeLocalStorage.getItem("T_ID");
    this.commonService.getUid("gtm-segment-service").pipe().subscribe({
      next: (Uid) => {
        this.selectedLanguage = Uid ?? "en";

      }
    });

    // LOTTOSOCIAL-PWA-1NB6: reset the defaults-pushed flag when the user successfully
    // leaves partial-registration. NavigationEnd (not NavigationStart) is used so
    // cancelled/redirected navigations do not incorrectly reset the flag before the user
    // has actually left. The type predicate ensures correct narrowing. Path segments are
    // matched with a regex so substring false-positives cannot prevent the reset.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(event => {
        // partialRegDefaultsPushed tracks only partial-registration visits; it is not
        // used by ensureJackpotPrizeSeed, which is idempotent on its own (it writes only
        // when jackpotPrize is missing/invalid), so these two checks are independent.
        if (!this.isPartialRegUrl(event.urlAfterRedirects)) {
          this.partialRegDefaultsPushed = false;
        }
        if (this.isPrizeSeedUrl(event.urlAfterRedirects)) {
          // SPA re-entry can race GTM History Change tags before LoginDataResolver
          // writes jackpotPrize — seed a safe object immediately (LOTTOSOCIAL-PWA-1NBD /
          // 1NC4). ensureJackpotPrizeSeed never overwrites valid existing data.
          this.ensureJackpotPrizeSeed(this.resolveProsubIdFromUrl(event.urlAfterRedirects));
        }
      });
  }

  /**
   * Safely accesses window.location properties to handle iOS Safari race conditions
   * during navigation when location object may not be fully initialized
   */
  private getSafeLocation() {
    try {
      if (typeof window === 'undefined' || !window.location) {
        return {
          pathname: '',
          href: '',
          search: '',
        };
      }
      return {
        pathname: window.location.pathname || '',
        href: window.location.href || '',
        search: window.location.search || '',
      };
    } catch (error) {
      this.authService.errorHandlerLogs('[GTMSegmentService] Error accessing window.location:', error);
      return {
        pathname: '',
        href: '',
        search: '',
      };
    }
  }

  /**
   * Returns true when `url` resolves to the /partial-registration route.
   * Strips query string and fragment before matching so that URLs like
   * /login?returnUrl=/partial-registration do not produce false positives.
   * Uses a segment-boundary regex built from PARTIAL_REG_ROUTE (which also
   * matches matrix params like /partial-registration;foo=bar) so the
   * constant is the single source of truth for the route name.
   */
  private isPartialRegUrl(url: string): boolean {
    const path = (url ?? '').split('?')[0].split('#')[0];
    return new RegExp(`(^|/)${this.PARTIAL_REG_ROUTE}(/|;|$)`).test(path);
  }

  /** See isGtmPrizeSeedPath (pure, unit-tested helper). */
  private isPrizeSeedUrl(url: string): boolean {
    return isGtmPrizeSeedPath(url);
  }

  /**
   * Resolves Prosub_ID from the current router URL / location search.
   * Prefers the provided URL so SPA NavigationEnd is not contaminated by a
   * lagged window.location.search.
   */
  private resolveProsubIdFromUrl(url?: string): string | null {
    try {
      const rawUrl = url || this.router?.url || '';
      const queryFromRouter = this.router?.parseUrl(rawUrl)?.queryParams ?? {};
      const searchFromUrl = rawUrl.includes('?')
        ? rawUrl.substring(rawUrl.indexOf('?'))
        : '';
      const search =
        searchFromUrl ||
        (typeof window !== 'undefined' ? this.getSafeLocation()?.search : '') ||
        '';
      const urlParams =
        typeof URLSearchParams !== 'undefined' ? new URLSearchParams(search) : null;

      const raw =
        queryFromRouter['Prosub_ID'] ||
        queryFromRouter['prosub_id'] ||
        urlParams?.get('Prosub_ID') ||
        urlParams?.get('prosub_id') ||
        null;
      return raw || null;
    } catch {
      return null;
    }
  }

  /**
   * Safe purchase object shape expected by GTM Axon custom HTML tags:
   *   var a = JSON.parse(localStorage.jackpotPrize); ... a.prize / a.offers.offer_name
   */
  private buildSafeJackpotPrize(
    prosubId: string | null,
    overrides?: Record<string, unknown>
  ): string {
    const base: {
      prize: number;
      offer_id: string;
      offers: { offer_name: string; [key: string]: unknown };
      prosub_id: string;
      checkout: boolean;
      [key: string]: unknown;
    } = {
      prize: 0,
      offer_id: "",
      offers: { offer_name: "" },
      prosub_id: prosubId ?? "",
      checkout: true,
      ...(overrides ?? {}),
    };
    if (!base.offers || typeof base.offers !== "object") {
      base.offers = { offer_name: "" };
    }
    const prizeNum = Number(base.prize);
    base.prize = Number.isFinite(prizeNum) ? prizeNum : 0;
    return JSON.stringify(base);
  }

  /**
   * Ensures localStorage.jackpotPrize parses to a non-null object with numeric prize
   * and a safe nested `offers` object. GTM macros read this key directly; missing/"null"
   * values cause TypeError: Cannot read properties of null (reading 'prize')
   * (LOTTOSOCIAL-PWA-1NBD).
   */
  private ensureJackpotPrizeSeed(prosubId: string | null = null): void {
    try {
      const raw = this.safeLocalStorage.getItem("jackpotPrize");
      let parsed: Record<string, unknown> | null = null;
      let needsFullSeed = !raw || raw === "null" || raw === "undefined";
      if (!needsFullSeed) {
        try {
          const candidate = JSON.parse(raw as string);
          needsFullSeed =
            candidate === null ||
            typeof candidate !== "object" ||
            Array.isArray(candidate);
          if (!needsFullSeed) {
            parsed = candidate;
          }
        } catch {
          needsFullSeed = true;
        }
      }
      if (needsFullSeed || !parsed) {
        this.safeLocalStorage.setItem(
          "jackpotPrize",
          this.buildSafeJackpotPrize(prosubId)
        );
        return;
      }

      let changed = false;
      const prizeNum = Number(parsed.prize);
      if (!Number.isFinite(prizeNum)) {
        parsed.prize = 0;
        changed = true;
      }
      if (
        !parsed.offers ||
        typeof parsed.offers !== "object" ||
        Array.isArray(parsed.offers)
      ) {
        parsed.offers = { offer_name: "" };
        changed = true;
      }
      if (prosubId && !parsed.prosub_id) {
        parsed.prosub_id = prosubId;
        changed = true;
      }
      if (changed) {
        this.safeLocalStorage.setItem("jackpotPrize", JSON.stringify(parsed));
      }
    } catch (err) {
      this.authService.errorHandlerLogs("[GTM] Failed to seed jackpotPrize:", err);
    }
  }

  /** Resolve current ProSub id from route params / local storage. */
  getHopeProsubId(explicitProsubId?: string | null): string {
    const fromArg = explicitProsubId != null ? String(explicitProsubId).trim() : "";
    if (fromArg) {
      return fromArg;
    }
    const fromService =
      this.prosub?.params?.Prosub_ID ??
      this.prosub?.params?.prosub_id ??
      this.prosubDetails?.prosub_id ??
      "";
    if (fromService) {
      return String(fromService).trim();
    }
    try {
      const fromRoute =
        this.actRoute?.snapshot?.queryParamMap?.get("prosub_id") ||
        this.actRoute?.snapshot?.queryParamMap?.get("Prosub_ID") ||
        "";
      if (fromRoute) {
        return String(fromRoute).trim();
      }
    } catch {
      // ignore route access errors
    }
    return (
      this.safeLocalStorage.getItem("prosub_id") ||
      localStorage.getItem("prosub_id") ||
      ""
    ).trim();
  }

  isHopeProsubJourney(prosubId?: string | null): boolean {
    return HOPE_PROSUB_IDS.has(this.getHopeProsubId(prosubId));
  }

  private ensureHopePixelInitialized(): boolean {
    if (typeof fbq === "undefined" || !fbq) {
      this.authService.errorHandlerLogs("[HopeMeta] fbq not defined ÔÇö skipping Pixel init");
      return false;
    }
    if (!this.hopePixelInitialized) {
      try {
        fbq("init", HOPE_META_PIXEL_ID);
        this.hopePixelInitialized = true;
      } catch (error) {
        this.authService.errorHandlerLogs("[HopeMeta] Pixel init failed:", error);
        return false;
      }
    }
    return true;
  }

  private createHopeEventId(): string {
    try {
      return uuidv4();
    } catch {
      return `hope_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
  }

  private hasHopeEventFired(storageKey: string): boolean {
    try {
      return sessionStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  }

  private markHopeEventFired(storageKey: string): void {
    try {
      sessionStorage.setItem(storageKey, "1");
    } catch {
      // ignore storage failures
    }
  }

  /**
   * Fire browser Pixel + CAPI for Hope ProSub 4793/4794 with a shared event_id.
   * Dedupes conversion milestones per browser session; PageView dedupes per URL+prosub.
   */
  trackHopeMetaEvent(
    eventName: "PageView" | "CompleteRegistration" | "AddPaymentInfo" | "Purchase",
    options: {
      prosubId?: string | null;
      value?: number;
      currency?: string;
      email?: string;
      phone?: string;
      force?: boolean;
    } = {}
  ): void {
    const prosubId = this.getHopeProsubId(options.prosubId);
    if (!this.isHopeProsubJourney(prosubId)) {
      return;
    }

    const safeLocation = this.getSafeLocation();
    const dedupeKey =
      eventName === "PageView"
        ? `hope_meta_${eventName}_${prosubId}_${safeLocation.pathname}`
        : `hope_meta_${eventName}_${prosubId}`;
    if (!options.force && this.hasHopeEventFired(dedupeKey)) {
      return;
    }

    if (!this.ensureHopePixelInitialized()) {
      return;
    }

    const eventId = this.createHopeEventId();
    const customData: Data = {};
    if (options.value !== undefined && options.value !== null) {
      customData.value = options.value;
    }
    if (options.currency) {
      customData.currency = options.currency;
    }

    try {
      if (Object.keys(customData).length > 0) {
        fbq("track", eventName, customData, { eventID: eventId });
      } else {
        fbq("track", eventName, {}, { eventID: eventId });
      }
    } catch (error) {
      this.authService.errorHandlerLogs(`[HopeMeta] browser ${eventName} failed:`, error);
    }

    const { email, phone } = this.resolveHopeUserContact(options);

    this.getV2CloudFunction("sendmetacapieventv2", {
      event_name: eventName,
      event_id: eventId,
      event_source_url: safeLocation.href || undefined,
      user_data: {
        email: email || undefined,
        phone: phone || undefined,
        client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      },
      custom_data: Object.keys(customData).length > 0 ? customData : undefined,
    })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.markHopeEventFired(dedupeKey);
        },
        error: (error) => {
          // Still mark fired after browser send to avoid Pixel spam on CAPI failures.
          this.markHopeEventFired(dedupeKey);
          this.authService.errorHandlerLogs(`[HopeMeta] CAPI ${eventName} failed:`, error);
        },
      });
  }

  /** PageView for Hope journey pages (browser + CAPI). */
  trackHopePageView(prosubId?: string | null): void {
    this.trackHopeMetaEvent("PageView", { prosubId });
  }

  /** CompleteRegistration after successful account create on Hope journey. */
  trackHopeCompleteRegistration(prosubId?: string | null, email?: string, phone?: string): void {
    this.trackHopeMetaEvent("CompleteRegistration", { prosubId, email, phone });
  }

  /**
   * Persist signup contact for Hope Meta CAPI advanced matching.
   * Called before navigating to account-created so CompleteRegistration has real values.
   */
  setHopeRegistrationUserData(email?: string | null, phone?: string | null): void {
    const normalizedEmail = (email ?? "").toString().trim();
    let normalizedPhone = (phone ?? "").toString().trim();
    if (normalizedPhone && !normalizedPhone.startsWith("+")) {
      normalizedPhone = `+${normalizedPhone.replace(/^\+/, "")}`;
    }

    if (normalizedEmail) {
      this.safeLocalStorage.setItem("email", normalizedEmail);
    }
    if (normalizedPhone) {
      this.safeLocalStorage.setItem("msn", normalizedPhone.replace(/^\+/, ""));
    }

    this.purchaseTagDetails$.next({
      ...(this.purchaseTagDetails$?.value || {}),
      email: normalizedEmail || this.purchaseTagDetails$?.value?.email || "",
      em: normalizedEmail || this.purchaseTagDetails$?.value?.em || "",
      phone: normalizedPhone || this.purchaseTagDetails$?.value?.phone || "",
    });
  }

  /** Resolve email/phone for Hope Meta from args, purchase tags, auth, or storage. */
  private resolveHopeUserContact(options: { email?: string; phone?: string } = {}): {
    email: string;
    phone: string;
  } {
    const tags = this.purchaseTagDetails$?.value || {};
    const email = (
      options.email ||
      tags.email ||
      tags.em ||
      this.safeLocalStorage.getItem("email") ||
      this.authService.segmentRequestData$?.value?.email ||
      ""
    )
      .toString()
      .trim();

    let phone = (
      options.phone ||
      tags.phone ||
      this.authService.userMSN$?.value ||
      this.safeLocalStorage.getItem("msn") ||
      ""
    )
      .toString()
      .trim();

    if (phone && !phone.startsWith("+")) {
      phone = `+${phone.replace(/^\+/, "")}`;
    }

    return { email, phone };
  }

  /** AddPaymentInfo (4793 10p) or Purchase (4794 ┬ú1) after confirmed payment. */
  trackHopePaymentConfirmed(prosubId?: string | null): void {
    const id = this.getHopeProsubId(prosubId);
    if (id === "4793") {
      this.trackHopeMetaEvent("AddPaymentInfo", {
        prosubId: id,
        value: 0.1,
        currency: "GBP",
      });
      return;
    }
    if (id === "4794") {
      this.trackHopeMetaEvent("Purchase", {
        prosubId: id,
        value: 1.0,
        currency: "GBP",
      });
    }
  }


  GTMInitilization() {
    // Seed jackpotPrize synchronously BEFORE getUid — GTM Axon / History Change tags
    // read localStorage.jackpotPrize.prize and can fire while auth UID resolution /
    // LoginDataResolver are still pending (LOTTOSOCIAL-PWA-1NBD).
    try {
      const earlyProsubId = this.resolveProsubIdFromUrl();
      if (
        this.isPrizeSeedUrl(this.router?.url || '') ||
        this.isPrizeSeedUrl(this.getSafeLocation().pathname)
      ) {
        this.ensureJackpotPrizeSeed(earlyProsubId);
      }
    } catch (seedErr) {
      this.authService.errorHandlerLogs('[GTM] Failed to seed prize defaults:', seedErr);
    }

    // If payment success redirected to 4C/profile before GTM consumed the event, replay once.
    this.replayPendingPaymentSuccessIfNeeded();

    this.commonService.getUid("gtm-segment-service").pipe(take(1)).subscribe({
      next: (uid) => {
        try {
          let pageName = this.router.url;
          pageName = pageName.substr(pageName.lastIndexOf("/") + 1, pageName?.length);

          const safeLocation = this.getSafeLocation();

          if (typeof analytics !== "undefined") {
            analytics?.track("Page_Events", {
              name: pageName,
              path: safeLocation.pathname,
              title: document?.title || '',
              url: safeLocation.href,
            });
          }

          this.analyticsFire.logEvent("Page_Events", {
            pageName: this.router.url,
          });

          const hopeJourneyUrl =
            this.router?.url?.includes("partial-registration") ||
            this.router?.url?.includes("account-created") ||
            this.router?.url?.includes("successful-payment") ||
            this.router?.url?.includes("free-reg-complete-profile") ||
            this.router?.url?.includes("free-reg-step4c") ||
            this.router?.url?.includes("payment");
          if (hopeJourneyUrl && this.isHopeProsubJourney()) {
            this.trackHopePageView();
          }

          if (
            this.router?.url?.includes("partial-registration") ||
            this.router?.url?.includes("account-created") ||
            this.router?.url?.includes("successful-payment") ||
            this.router?.url?.includes("free-reg-complete-profile") ||
            this.router?.url?.includes("free-reg-step4c")
          ) {
            this.actRoute.queryParamMap.pipe(take(1)).subscribe(
              (params: any) => {
                // Prefer ParamMap.get() (LOTTOSOCIAL-PWA-1NBD); keep .params fallback for
                // any legacy wrapper shape still used by formPaymentDataForTopUp.
                const fromMap =
                  params?.get?.("Prosub_ID") ||
                  params?.get?.("prosub_id") ||
                  params?.params?.Prosub_ID ||
                  params?.params?.prosub_id;
                if (fromMap || this.isPartialRegUrl(this.router?.url || "")) {
                  this.prosub = params;
                }
              },
              (err) => {
                this.authService.errorHandlerLogs(
                  "[GTM] Error reading queryParamMap on GTMInitilization:",
                  err
                );
              }
            );

            const isPartialRegistration = this.isPartialRegUrl(this.router?.url ?? "");
            // Resolve Prosub_ID from the *current* navigation URL first. Checking
            // this.prosub before the URL allowed a stale product ID from a prior SPA
            // visit to win when the current partial-registration has no Prosub_ID
            // (github-actions review on LOTTOSOCIAL-PWA-1NAK).
            const rawProsubID =
              this.resolveProsubIdFromUrl() ||
              this.prosub?.get?.("Prosub_ID") ||
              this.prosub?.get?.("prosub_id") ||
              this.prosub?.params?.Prosub_ID ||
              null;
            // Treat blank strings the same as absent so GTM gets a cleared value.
            const capturedProsubID = rawProsubID || null;

            // LOTTOSOCIAL-PWA-1NAK / 1NAJ / 1NB6 / 1NBD: always seed numeric prize on
            // partial-registration — including when Prosub_ID is absent — so GTM
            // custom tags never evaluate `.prize` on a null data-layer variable or
            // a null jackpotPrize parse result.
            if (isPartialRegistration) {
              this.ensureJackpotPrizeSeed(capturedProsubID);
              // Always set Prosub_Id (null when absent) so GTM does not retain a
              // stale product ID from a prior SPA navigation.
              dataLayer.push({
                Prosub_Id: capturedProsubID,
                prosub_price: 0,
                prize: 0,
                prosub_price_valid: false,
              });
            }

            if (
              (capturedProsubID && isPartialRegistration) ||
              this.router?.url?.includes("account-created")
            ) {
              if (isPartialRegistration && capturedProsubID) {
                  this.getLSWProsubDetails(capturedProsubID).subscribe(
                    (prosubDetails) => {
                      // Resolve to a plain data object. DocumentSnapshot.data() returns
                      // undefined when the document does not exist; treat that the same as
                      // a missing document rather than propagating null/undefined downstream.
                      if (prosubDetails && typeof prosubDetails.data === 'function') {
                        // Fall back to {} when the document does not exist so undefined
                        // never propagates to downstream code via this.prosubDetails.
                        this.prosubDetails = prosubDetails.data() ?? {};
                      } else {
                        // prosubDetails might already be the plain data object (not a DocumentSnapshot).
                        // Fall back to empty object so null never propagates into GTM tag pipeline.
                        this.prosubDetails = prosubDetails ?? {};
                      }

                      const rawPrice = this.prosubDetails?.price;
                      // Normalise to a number: accept stored numbers directly, and also
                      // coerce numeric strings (e.g. "10") that Firestore may return.
                      // Blank/whitespace strings are rejected explicitly — Number('') === 0
                      // would otherwise look like a valid free product.
                      const trimmedPrice = typeof rawPrice === 'string' ? rawPrice.trim() : rawPrice;
                      const numericPrice = typeof trimmedPrice === 'number'
                        ? trimmedPrice
                        : (typeof trimmedPrice === 'string' && trimmedPrice !== '') ? Number(trimmedPrice) : NaN;
                      // A price of 0 is intentionally valid for free/pre-auth products.
                      // Negative prices are not valid in this domain.
                      const hasNumericPrice = Number.isFinite(numericPrice) && numericPrice >= 0;

                      // Always push the prosub price so GTM variables are never stale.
                      // Include prosub_price_valid so GTM tags can distinguish a genuine
                      // zero-price product from an unavailable/error price (both send 0).
                      // LOTTOSOCIAL-PWA-1NAJ: always include top-level `prize` (never omit) so
                      // GTM custom tags never read `.prize` on a null data-layer variable.
                      const safePrice = hasNumericPrice ? numericPrice : 0;
                      dataLayer.push({
                        event: "prosub_details_loaded",
                        Prosub_Id: capturedProsubID,
                        prosub_price: safePrice,
                        prize: safePrice,
                        prosub_price_valid: hasNumericPrice,
                      });
                      // Keep jackpotPrize in sync so Axon tags that re-read localStorage
                      // after prosub_details_loaded see a numeric prize (LOTTOSOCIAL-PWA-1NBD).
                      this.safeLocalStorage.setItem(
                        "jackpotPrize",
                        this.buildSafeJackpotPrize(capturedProsubID, {
                          prize: safePrice,
                          offer_id: this.prosubDetails?.offer_id ?? "",
                          offers:
                            this.prosubDetails && typeof this.prosubDetails === "object"
                              ? {
                                  ...this.prosubDetails,
                                  offer_name: this.prosubDetails?.offer_name ?? "",
                                }
                              : { offer_name: "" },
                          prosub_id:
                            this.prosubDetails?.prosub_id ?? capturedProsubID ?? "",
                        })
                      );

                      // Only fire advanced_matching when the price is valid. GTM tag chains
                      // triggered by advanced_matching read prosub_price/purchase data; if
                      // price is missing the onSuccess callback would access .price on a null
                      // purchase object (LOTTOSOCIAL-PWA-1NAK). A price of 0 is valid here.
                      if (hasNumericPrice) {
                        // Read phone/email at callback time so the latest
                        // purchaseTagDetails$ values are used (not a stale snapshot).
                        dataLayer.push({
                          event: "advanced_matching",
                          c_customer_id: uid,
                          phone: this.purchaseTagDetails$?.value?.phone,
                          em: this.purchaseTagDetails$?.value?.email ?? this.purchaseTagDetails$?.value?.em,
                        });
                      } else {
                        this.authService.errorHandlerLogs(`[GTM] prosubDetails.price is missing, negative, or non-numeric for prosubId: ${capturedProsubID} — skipping advanced_matching to prevent GTM tag chain crash`);
                      }
                    },
                    (err) => {
                      this.authService.errorHandlerLogs('[GTM] Error loading prosub details:', err);
                      // Push safe defaults so GTM variables are not stale from a prior push.
                      // prosub_price_valid: false distinguishes this from a genuine free product.
                      // Always include prize: 0 (LOTTOSOCIAL-PWA-1NAJ).
                      dataLayer.push({
                        event: "prosub_details_error",
                        Prosub_Id: capturedProsubID,
                        prosub_price: 0,
                        prize: 0,
                        prosub_price_valid: false,
                      });
                    }
                  );
              } else if (this.router?.url?.includes("account-created")) {
                // For account-created page, push advanced_matching without waiting for prosub details
                dataLayer.push({
                  event: "advanced_matching",
                  c_customer_id: uid,
                  phone: this.purchaseTagDetails$?.value?.phone,
                  em: this.purchaseTagDetails$?.value?.email,
                });
                if (this.isHopeProsubJourney()) {
                  // Email/phone resolved inside trackHopeMetaEvent from tags/storage/auth.
                  this.trackHopeCompleteRegistration();
                }
              }
            }

            if (this.router?.url?.includes("successful-payment")) {
              const hopeProsubId = this.getHopeProsubId(
                this.prosub?.params?.Prosub_ID ??
                  this.prosub?.params?.prosub_id ??
                  this.prosubDetails?.prosub_id
              );
              if (this.isHopeProsubJourney(hopeProsubId)) {
                // Hope 4793/4794: AddPaymentInfo / Purchase with GBP values (not generic Purchase/Subscribe).
                this.trackHopePaymentConfirmed(hopeProsubId);
              } else if (typeof fbq !== "undefined" &&
                this.prosubDetails &&
                typeof this.prosubDetails === 'object' &&
                this.prosubDetails.offer_id) {
                fbq?.("track", "Purchase", {
                  Offer_Id: this.prosubDetails.offer_id,
                  Prosub_Id:
                    this.prosub?.get?.("Prosub_ID") ??
                    this.prosub?.get?.("prosub_id") ??
                    this.prosub?.params?.Prosub_ID ??
                    this.prosubDetails?.prosub_id,
                  value: 123,
                  currency: "rs",
                  p_type: "credit",
                  customer_id: uid,
                });

                fbq?.("track", "Subscribe", {
                  value: this.prosubDetails?.price,
                  currency: "GBP",
                });
              } else if (!this.isHopeProsubJourney(hopeProsubId)) {
                if (typeof fbq === "undefined") {
                  this.authService.errorHandlerLogs('[GTM] Skipping Facebook Pixel tracking - fbq not defined');
                } else if (!this.prosubDetails) {
                  this.authService.errorHandlerLogs('[GTM] Skipping Facebook Pixel tracking - prosubDetails not loaded yet');
                } else if (typeof this.prosubDetails !== 'object') {
                  this.authService.errorHandlerLogs('[GTM] Skipping Facebook Pixel tracking - prosubDetails is not an object');
                } else if (!this.prosubDetails.offer_id) {
                  this.authService.errorHandlerLogs('[GTM] Skipping Facebook Pixel tracking - offer_id is null/undefined');
                }
              }
            }
          }
        } catch (error) {
          this.authService.errorHandlerLogs('[GTMInitilization] Error during analytics tracking:', error);
        }
      },
    });
  }


  getLSWProsubDetails(id: any) {
    if (!id) return of(null); // Return null if no id is provided
    // Reference the Firestore document
    const path_id = "ERzajuwYcQgv2qQ1tA9q";
    const dv = { docId: id };
    const method = "read";

    return this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      dv,
      method,
    }).pipe(
      map((snapshot: Data) => {
        // Add null check to ensure snapshot and data exist
        if (!snapshot || !snapshot.data) {
          this.authService.errorHandlerLogs('[GTM] getLSWProsubDetails returned null/undefined data for id:', id);
          return null;
        }
        return snapshot;
      }),
      catchError((error) => {
        this.authService.errorHandlerLogs('[GTM] Error fetching prosub details for id:',error);
        return of(null);
      })
    );
  }

  formPaymentDataForTopUp(offer: any) {
    // Add defensive null check for offer parameter
    if (!offer || typeof offer !== 'object') {
      this.authService.errorHandlerLogs('[GTM] formPaymentDataForTopUp called with null/invalid offer:', offer);
      // prize must be a finite number and offers a non-null object — GTM Axon tags
      // JSON.parse(localStorage.jackpotPrize) and read .prize / .offers.offer_name
      // (LOTTOSOCIAL-PWA-1NBD).
      return {
        prosub_id: "",
        phone: "",
        prize: 0,
        offers: { offer_name: "" },
        checkout: true,
        draw: "",
        game: "",
        line: "",
        offer_id: "",
        real_name: "",
        page: "store",
        TP1: "",
        TP2: "",
        TP3: "",
        T_ID: "",
        other: "",
        vc: this.vc || "",
      };
    }

    const rawPrice = offer?.price;
    const numericPrice =
      typeof rawPrice === "number"
        ? rawPrice
        : typeof rawPrice === "string" && rawPrice.trim() !== ""
          ? Number(rawPrice)
          : NaN;
    const safePrize = Number.isFinite(numericPrice) ? numericPrice : 0;

    return {
      prosub_id: offer?.prosub_id ?? "",
      phone: offer?.phone ?? "",
      prize: safePrize,
      offers: offer && typeof offer === "object" ? offer : { offer_name: "" },
      checkout: true,
      draw: "",
      game: "",
      line: "",
      offer_id: offer?.offer_id ?? "",
      real_name: "",
      page: "store",
      TP1: this.prosub && this.prosub.params && this.prosub.params.TP1 ? this.prosub.params.TP1?.toLowerCase() : "",
      TP2: this.prosub && this.prosub.params && this.prosub.params.TP2 ? this.prosub.params.TP2?.toLowerCase() : "",
      TP3:
        this.prosub && this.prosub.params
          ? this.prosub.params.TP3
            ? this.prosub.params.TP3?.toLowerCase()
            : this.prosub.params[
              Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "tp3")
            ]?.toLowerCase() ?? ""
          : "",
      T_ID:
        this.prosub && this.prosub.params
          ? this.prosub.params.T_ID
            ? this.prosub.params.T_ID?.toLowerCase()
            : this.prosub.params[
              Object.keys(this.prosub.params)?.find((key) => key?.toLowerCase() === "t_id")
            ]?.toLowerCase() ?? ""
          : "",
      other:
        this.prosub && this.prosub.params && this.prosub.params.Other ? this.prosub.params.Other?.toLowerCase() : "",
      vc: this.vc ?? "",
    };
  }

  async saleRedirectionByProsub(prosub_id: any, vc: any, matDialog) {
    // Add defensive check for prosubDetails and optional_page
    if (!this.prosubDetails || typeof this.prosubDetails !== 'object' || !this.prosubDetails.optional_page) {
      // this.getGlobalProsub().subscribe((d: any) => {});
      this.prosub = this.safeLocalStorage.getItem("prosub_id");
      if (!this.prosub) {
        this.prosub = await this.getGlobalProsub().toPromise();
      }
      this.safeLocalStorage.setItem("prosub_id", this.prosub);

      // Only fetch prosub details if not already loaded
      if (!this.prosubDetails && this.prosub) {
        try {
          this.prosubDetails = await this.getLSWProsubDetails(this.prosub).toPromise();
        } catch (error) {
          this.authService.errorHandlerLogs('[GTM] Error loading prosub details in saleRedirectionByProsub:', error);
          this.prosubDetails = null;
        }
      }

      // this.getLSWProsubDetails(this.prosub).subscribe((prosubDetails) => { });
      // this.prosubDetails = prosubDetails.data();

      // Only call formPaymentDataForTopUp if prosubDetails is valid
      if (this.prosubDetails && typeof this.prosubDetails === 'object') {
        this.userProduct$.next(this.formPaymentDataForTopUp(this.prosubDetails));
      } else {
        this.authService.errorHandlerLogs('[GTM] Skipping formPaymentDataForTopUp - prosubDetails is null/invalid');
      }

      this.callINT1CommonMethod(matDialog, vc, prosub_id);
      //this.loading.dismiss();
    } else {
      this.callINT1CommonMethod(matDialog, vc, prosub_id);

      // Add null check before calling formPaymentDataForTopUp
      if (this.prosubDetails && typeof this.prosubDetails === 'object') {
        this.userProduct$.next(this.formPaymentDataForTopUp(this.prosubDetails));
      } else {
        this.authService.errorHandlerLogs('[GTM] Skipping formPaymentDataForTopUp - prosubDetails is null/invalid');
      }

      this.loading.dismiss();
    }
  }
  private callINT1CommonMethod(matDialog: any, vc: any, prosub_id: any): void {
    this.callInt1()
      .then(async (resInt: string) => {
        if (vc && resInt.indexOf("/free-reg-step4c") < 0) {
          this.router.navigate([resInt], {
            queryParams: { prosub_id: prosub_id, vc: vc, ...this.queryParamsToNavigate },
          });
        } else {
          if (resInt.indexOf("/free-reg-step4c") > -1) {
            const authService = this.injector.get(AuthPartialRegService);
            authService.partial_Reg(prosub_id, "", "").subscribe((res) => {
              this.router.navigate([resInt], {
                queryParams: { prosub_id: prosub_id, ...this.queryParamsToNavigate },
                state: res,
              });
            });
            return;
          }

          this.router.navigate([resInt], {
            queryParams: { prosub_id: prosub_id, ...this.queryParamsToNavigate },
          });
        }

        // Uncomment if loading service needs dismissal after navigation
        // this.loading.dismiss();
      })
      .catch((error) => {
        const authService = this.injector.get(AuthService);
        const path_id = "G2hzy28FznIF29mIsjoR";
        const filter = ["static_messages_pwa"];
        const method = "read";

        authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          filter,
          method,
        }).subscribe({
          next: (response: any) => {
            this.translatedToastMsg = response?.data?.static_messages_pwa;
            const msg = this.translatedToastMsg?.["1014"]?.[this.selectedLanguage];

            authService
              .getConstantsMsg("1014")
              .then((translatedMsg) => {
                const defaultMsg = {
                  message: {
                    line_1: "Something went wrong",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                authService.showToastMessage(translatedMsg || msg || "Something went wrong", defaultMsg);
              })
              .catch((err) => {
                this.authService.errorHandlerLogs("Error fetching constants message:", err);
              });
          },
          error: (err) => {
            this.authService.errorHandlerLogs("Error fetching constants from CF:", err);
          },
        });

        this.loading.dismiss();
        this.authService.errorHandlerLogs("Error in callINT1CommonMethod:", error);
        return error;
      });

  }

  callInt1(prosubId: string = ""): Promise<string> {
    return new Promise((resolve, reject) => {
      this.commonService.getUid("callInt1").pipe(take(1)).subscribe({
        next: (uid) => {
          const requestData = {
            userId: uid,
            prosub_id: prosubId || this.safeLocalStorage.getItem("prosub_id"),
            TP1: this.TP1 ?? "",
            TP2: this.TP2 ?? "",
            TP3: this.TP3 ?? "",
            T_ID: this.T_ID ?? "",
          };

          this.getCallInt1(requestData)
            .pipe(take(1))
            .subscribe({
              next: (res: any) => {
                if (res?.status === "success") {
                  resolve(res?.post_name ?? "");
                } else {
                  resolve("");
                }
              },
              error: () => reject(false),
            });
        },
        error: () => reject(false),
      });
    });
  }


  // getPostNameFromInt1(requestData) {
  //   return this.angularFireFunctions.httpsCallable("interstitialOne")(requestData);
  // }
  getCallInt1(requestData) {
    return this.authService.getV2CloudFunction("callinterstitialonev2", requestData);
    // return this.angularFireFunctions.httpsCallable("callinterstitialone")(requestData);
  }
  //Set clevertap identity with the user details.
  setUserIdentify(user: any) {
    try {
      if (!user || typeof analytics === "undefined") return;

      let phoneNumber = user?.msn || "";

      // Make sure phoneNumber is a string, else convert it
      if (typeof phoneNumber !== "string") phoneNumber = String(phoneNumber);

      // Remove leading "0"
      if (phoneNumber.startsWith("0")) phoneNumber = phoneNumber.substring(1);

      // Prepend country code if missing
      if (!phoneNumber.startsWith(user?.country_code)) {
        phoneNumber = user?.country_code + phoneNumber;
      }

      this.commonService.getUid("setUserIdentify").pipe(take(1)).subscribe({
        next: (uid) => {
          if (!uid) return;
          analytics?.identify(uid, {
            email: user?.email ?? "",
            name: user?.name ?? "",
            phone: phoneNumber ? "+" + phoneNumber : "",
          });
        },
        error: (err) => {
          this.authService.errorHandlerLogs("[setUserIdentify] Failed to get UID:", err);
        },
      });
    } catch (error) {
      this.authService.errorHandlerLogs("[setUserIdentify] Exception:", error);
      return error;
    }
  }

  GTMPopupInitialization(msg: any, body?: any) {
    try {
      if (typeof analytics !== "undefined") {
        analytics?.track(msg, {
          data: msg,
          ...(body != null ? { message: body } : {}),
        });
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[GTMPopupInitialization] Error tracking analytics:', error);
    }
  }
  trackEvent(msg: string, body?: Data) {
    try {
      if (typeof analytics === "undefined") return;

      this.commonService.getUid("trackEvent").pipe(take(1)).subscribe({
        next: (uid) => {
          if (!uid) return;

          analytics?.identify(uid);

          analytics?.track(msg, {
            ...body,
          });
        },
        error: (err) => {
          this.authService.errorHandlerLogs("[trackEvent] Failed to get UID:", err);
        },
      });
    } catch (error) {
      this.authService.errorHandlerLogs("[trackEvent] Exception:", error);
      return;
    }
  }


  getGlobalProsub(): Observable<any> {
    // Reference the Firestore document
    const systemReferenceDocRef = doc(this.firestore, 'general_modules/system_reference');
    // Use onSnapshot for real-time updates and convert into an Observable
    return new Observable((subscriber) => {
      const unsubscribe = onSnapshot(
        systemReferenceDocRef,
        (snapshot) => {
          subscriber.next(snapshot.data()); // Emit document data
        },
        (error) => {
          subscriber.error(error); // Handle errors gracefully
        }
      );
      return () => unsubscribe(); // Cleanup subscription
    }).pipe(
      map((data) => data || null), // Process the data from Firestore
      shareReplay(1) // Cache the response for reuse
    );
  }

  getProsubDetails(prosub_id: string) {
    if (!prosub_id) {
      this.authService.errorHandlerLogs("Prosub ID is required.");
      return from(Promise.resolve(null)); // Return an Observable that resolves to null if no ID is provided
    }

    // Reference the Firestore document for prosub_id details
    const path_id = "ERzajuwYcQgv2qQ1tA9q";
    const dv = { docId: prosub_id };
    const method = "read";

    return this.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      dv,
      method,
    }).pipe(map((snapshot: any) => snapshot?.data));
  }


  getCappingStatus(offerId: string) {
    if (!offerId) {
      this.authService.errorHandlerLogs("Invalid Offer ID.");
      return of(null);
    }

    return this.commonService.getUid("getCappingStatus").pipe(
      switchMap((userId) => {
        if (!userId) {
          this.authService.errorHandlerLogs("Invalid User ID.");
          return of(null);
        }

        // Reference the Firestore document for offer capping
        const docRef = doc(this.firestore, `users/${userId}/offer_capping/${offerId}`);

        // Convert Firestore Promise -> Observable
        return from(getDoc(docRef));
      })
    );
  }


  getOfferCardsDetails() {
    // Reference the Firestore document for offer cards details
    const offerCardDocRef = doc(this.firestore, "/general_modules/free_reg_step4a_offer_card_new");
    // Fetch the document data using getDoc() and convert it into an Observable
    return from(getDoc(offerCardDocRef));
  }

  GTM_CJ_site_pages(page: any) {
    this.commonService.getUid("GTM_CJ_site_pages").subscribe((userId) => {
      const email = this.safeLocalStorage.getItem("email");

      if (!userId || userId === "null") return;

      // Push the event to GTM dataLayer
      dataLayer.push({
        event: "cj_site_page",
        pageType: page,
        c_customer_id: userId,
        marketing_Channel: "Affiliate",
        order_Subtotal: "0.00",
        Email_Hash: email ?? ""
      });
    });
  }


  GTM_CJ_conversion(
    amount: any,
    currency: any,
    offer_id: any,
    customer_status: any,
    customer_country: any,
    p_type: any,
    prosub_id: any,
    customer_purchase_id: any,
    purchaseContext?: PaymentPurchaseContext
  ) {
    // Push immediately — do not wait on getUid auth gates (can take several seconds
    // or fail), which previously caused payment_success to be dropped after UI success.
    let resolvedUserId = "";
    try {
      const authUid = getAuth()?.currentUser?.uid;
      if (authUid) {
        resolvedUserId = String(authUid);
      }
    } catch {
      resolvedUserId = "";
    }
    if (!resolvedUserId) {
      try {
        const fromStorage =
          this.safeLocalStorage.getItem("uid") ||
          this.safeLocalStorage.getItem("user_id") ||
          "";
        if (fromStorage && fromStorage !== "null") {
          resolvedUserId = String(fromStorage);
        }
      } catch {
        // ignore
      }
    }

    const resolvedContext: PaymentPurchaseContext = mergePaymentPurchaseContext(
      purchaseContext,
      {
        purchaseDetail: this.userProduct$?.getValue?.() ?? this.userProduct$?.value,
        altprosubDetails: this.prosubDetails,
      }
    );
    const resolved = resolvePaymentAmountAndOffer(
      amount,
      offer_id,
      currency,
      resolvedContext
    );
    const cjSubTotal = resolved.subTotal;
    const cjCurrency = resolved.currency || "GBP";
    const resolvedProsubId = firstPresentString(
      prosub_id,
      resolvedContext.altprosubDetails?.prosub_id,
      resolvedContext.purchaseDetail?.prosub_id
    );
    const resolvedOfferId = resolved.offerId;
    const resolvedPurchaseId =
      customer_purchase_id !== undefined &&
      customer_purchase_id !== null &&
      String(customer_purchase_id) !== "" &&
      String(customer_purchase_id) !== "null"
        ? String(customer_purchase_id)
        : "";

    const dedupeKey =
      `${resolvedProsubId}|${cjSubTotal}|${resolvedOfferId}|${resolvedPurchaseId}`;
    if (dedupeKey && this.lastPaymentSuccessDedupeKey === dedupeKey) {
      return;
    }
    this.lastPaymentSuccessDedupeKey = dedupeKey;

    const email = this.safeLocalStorage.getItem("email");
    let emailHash: string | null = null;

    if (email) {
      // Hash the email using SHA-256
      emailHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);
    }

    const cj_products = [
      {
        item_id: resolvedOfferId,
        item_name: resolvedOfferId,
        price: cjSubTotal,
        quantity: 1,
      },
    ];

    const paymentSuccessPayload = {
      event: "payment_success",
      subTotal: cjSubTotal,
      coupon: "",
      discount: "0.00",
      currency: cjCurrency,
      orderid: resolvedOfferId || offer_id,
      customer_status: customer_status,
      customer_country: customer_country,
      customer_purchase_id: resolvedPurchaseId,
      email_sha256: emailHash,
      // Ecommerce vars required by GTM / AppLovin purchase mapping
      prosub_id: resolvedProsubId,
      cj_currency: cjCurrency,
      cj_subTotal: cjSubTotal,
      cj_products: cj_products,
      c_customer_id: resolvedUserId,
      products: [
        {
          productTitle: resolvedUserId, // ✅ replaced localStorage
          productType: p_type,
          productBrand: resolvedProsubId,
          productSku: resolvedOfferId,
          productPrice: cjSubTotal,
          productQuantity: "1",
          productDiscount: "0.00",
          // Also expose AppLovin item shape on products in case GTM maps items from it
          item_id: resolvedOfferId,
          item_name: resolvedOfferId,
          price: cjSubTotal,
          quantity: 1,
        },
      ],
    };

    this.persistPendingPaymentSuccess(paymentSuccessPayload);

    if (!window?.dataLayer) {
      window.dataLayer = [];
    }

    // Push both events in the same turn so a 4C/profile redirect cannot drop CJpurchase.
    window.dataLayer.push(paymentSuccessPayload);
    window.dataLayer.push({
      event: "CJpurchase",
    });

    // HATDEV-100772: bounded payment_succeeded; cross-service dedupe in PosthogService
    this.posthogService.emitPaymentSucceeded(
      buildPaymentSucceededProps({
        currency: cjCurrency,
        subTotal: cjSubTotal,
        offerId: resolvedOfferId,
        prosubId: resolvedProsubId,
        customerStatus: customer_status,
        customerCountry: customer_country,
        customerPurchaseId: resolvedPurchaseId,
        productType: p_type,
        customerId: resolvedUserId,
        context: resolvedContext,
      }),
      dedupeKey
    );
  }

  private lastPaymentSuccessDedupeKey: string | null = null;
  private readonly pendingPaymentSuccessStorageKey = "gtm_pending_payment_success";

  /** Offer price may be a number, numeric string, or a currency map like { GBP: 1 }. */
  private coercePurchaseAmount(amount: any): number {
    return firstPurchaseAmount(amount);
  }

  private persistPendingPaymentSuccess(payload: Record<string, unknown>): void {
    try {
      sessionStorage.setItem(this.pendingPaymentSuccessStorageKey, JSON.stringify(payload));
    } catch {
      // ignore storage quota / private-mode failures
    }
  }

  private replayPendingPaymentSuccessIfNeeded(): void {
    const url = this.router?.url || "";
    const isPostPaymentRoute =
      url.includes("free-reg-step4c") ||
      url.includes("free-reg-step-4c") ||
      url.includes("free-reg-complete-profile") ||
      url.includes("successful-payment");
    if (!isPostPaymentRoute) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(this.pendingPaymentSuccessStorageKey);
      if (!raw) {
        return;
      }
      sessionStorage.removeItem(this.pendingPaymentSuccessStorageKey);
      const payload = JSON.parse(raw);
      if (!payload || payload.event !== "payment_success") {
        return;
      }

      if (!window?.dataLayer) {
        window.dataLayer = [];
      }

      const purchaseId = String(payload.customer_purchase_id || "");
      const alreadyPushed = window.dataLayer.some(
        (entry: { event?: string; customer_purchase_id?: string }) =>
          entry?.event === "payment_success" &&
          String(entry?.customer_purchase_id || "") === purchaseId
      );
      if (alreadyPushed) {
        return;
      }

      window.dataLayer.push(payload);
      window.dataLayer.push({
        event: "CJpurchase",
      });
    } catch {
      // ignore parse / storage failures
    }
  }

  HotJarIdentify(user: any, ip: any) {
    this.commonService.getUid("HotJarIdentify").subscribe((userId) => {
      if (!userId || userId === "null") return;
    
      if (!window?.dataLayer) {
        window.dataLayer = [];
      }
      this.clarityService.syncAuthenticatedUser(userId, user?.pwa_segment_status);
      window.dataLayer.push({
        event: "hotJarIdentify",
        hj_name: user?.name,
        hj_user_id: userId,
        hj_email: user?.email ?? this.safeLocalStorage.getItem("email"),
        hj_ip_address: ip,
        hj_language: user?.language_code,
        hj_user_state: user?.onboard_state,
        hj_segment: user?.pwa_segment_status,
        hj_registered_since: user?.timestamp?.toDate().toDateString(),
      });
    });
  }


  FB_advanced_matching(
    phone: any,
    email: any,
    first_name: any,
    last_name: any,
    dob: any
  ) {
    this.commonService.getUid("FB_advanced_matching").subscribe((userId) => {
      if (!userId || userId === "null") return;

      dataLayer.push({
        event: "advanced_matching",
        c_customer_id: userId,
        phone,
        em: email ?? this.safeLocalStorage.getItem("email"),
        fn: first_name,
        ln: last_name,
        db: dob,
      });
    });
  }


  GTM_account_created(phoneNumber?: string) {
    const timeout = 10000;

    setTimeout(() => {
      this.commonService.getUid("GTM_account_created").subscribe((userId) => {
        if (!userId || userId === "null") return;

        dataLayer.push({
          event: "registration_success",
          c_customer_id: userId, // ✅ from commonService
          msn: phoneNumber,
        });

        // HATDEV-100772: PostHog mirror of GTM registration_success (deduped with signup paths)
        this.posthogService.emitRegistrationCompleted("gtm_registration_success", userId);
      });
    }, timeout);
  }


  GTM_4c_page() {
    this.commonService.getUid("GTM_4c_page").subscribe((userId) => {
      if (!userId || userId === "null") return;

      dataLayer.push({
        event: "4c_page",
        c_customer_id: userId, // ✅ userId from commonService
      });
    });
  }


  HotJarEvent(event: any) {
    this.commonService.getUid("HotJarEvent").subscribe((userId) => {
      if (!userId || userId === "null") return;
      if (event === "user_played_game") {
        this.clarityService.event("play_game");
      }

      dataLayer.push({
        event: "hotjar_event",
        hj_event: event,
      });
    });
  }

  // This method has been used to track the Home Card events
  HomeCard_Event(msg: string, currentCardDetails: Data, currentCardPosition: number) {
    try {
      // This variable contain card details
      const card_Id = currentCardDetails?.limb_msg_id || currentCardDetails?.card_id;
      const card_name = currentCardDetails?.card_type || currentCardDetails?.cardType;
      const card_position = currentCardPosition;
      // This condition has been
      if (card_Id && card_name && card_position != null && typeof analytics !== "undefined") {
        this.clarityService.event("click_homecard");
        analytics.track(msg, {
          Card_ID: card_Id,
          Card_Name: card_name,
          Card_Position: card_position,
        });
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[HomeCard_Event] Error tracking analytics:', error);
    }
  }
  GTMPopupInitializationForInbox(msg: string, body?: Data) {
    try {
      if (typeof analytics !== "undefined") {
        analytics.track(msg, {
          data: msg,
          ...(body != null ? { message: body } : {}),
        });
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[GTMPopupInitializationForInbox] Error tracking analytics:', error);
    }
  }

  GTMPopupInitializationForInboxNew(msg: string, body?: Data) {
    try {
      if (typeof analytics !== "undefined") {
        analytics.track(msg, {
          ...body,
        });
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[GTMPopupInitializationForInboxNew] Error tracking analytics:', error);
    }
  }

  // To Track Click on 4x payment pages so we can distinguish if low reg to sale conversion
  // is due to users dropping off or a potential payment issue.
  public fourxCtaClicked(): void {
    try {
      const currentUrl = this.router.url;

      this.commonService.getUid("fourxCtaClicked").subscribe((userId) => {
        if (!userId || userId === "null") return;

        if (currentUrl && typeof analytics !== "undefined") {
          try {
            analytics.track("4x_cta_clicked", {
              current_url: currentUrl,
            });
          } catch (analyticsError) {
            this.authService.errorHandlerLogs("[fourxCtaClicked] Analytics tracking error:", analyticsError);
          }
        }
      });
    } catch (error) {
      this.authService.errorHandlerLogs("[fourxCtaClicked] error:", error);
    }
  }

  // To Track Click on Add Payment Card
  GTMPopupInitializationForAddPayment() {
    try {
      if (typeof analytics !== "undefined") {
        analytics.track("addpaymentcard_clicked");
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[GTMPopupInitializationForAddPayment] Error tracking analytics:', error);
    }
  }

  //Collect visitor data and send to cloud function to save in bigquery.
  async collectVisitorData(
    password_reset_email: string | null = null,
    password_reset_msn: string | null = null,
    login_attempt_count: number | null = null,
    authentication_outcome: number | null = null
  ) {
    try {
      // Use safe location helper to avoid iOS Safari race conditions
      const safeLocation = this.getSafeLocation();

      // Extract URL parameters
      const urlParams = new URLSearchParams(safeLocation.search);
      const params: { [key: string]: string } = {};
      urlParams.forEach((value, key) => (params[key] = value));

      const urlParamsJsonString = JSON.stringify(params);

      // Extract and parse user agent data
      const userAgentString = navigator.userAgent;
      const userAgentData = this.parseUserAgent(userAgentString);
      const userAgentDataJsonString = JSON.stringify(userAgentData);

      // Fetch 'ajs_user_traits' from local storage and parse it
      const ajsUserTraits = this.safeLocalStorage.getItem("ajs_user_traits");
      let phone = "";
      if (ajsUserTraits) {
        try {
          const userTraitsData = JSON.parse(ajsUserTraits);
          phone = userTraitsData.phone || "";
        } catch {
          phone = "";
        }
      }

      // Fetching fingerprint data
      const fpPromise = FingerprintJS.load();
      const fp = await fpPromise;
      const result = await fp.get();

      if (!sessionStorage.getItem("initial_url")) {
        sessionStorage.setItem("initial_url", safeLocation.href);
      }

      const initialUrl = sessionStorage.getItem("initial_url") ?? "";
      const lastStartPage = this.getLastPage("start");
      const lastPageBeforeAuth = this.getLastPage("beforeAuth");

      if (!sessionStorage.getItem("SessionId") || sessionStorage.getItem("SessionId") === "null") {
        sessionStorage.setItem("SessionId", uuidv4());
      }

      // Wait for userId from commonService
this.commonService.getUid("collectVisitorData").subscribe(async (userId) => {
        if (!userId || userId === "null") userId = "";

            // Ensure firebase auth is ready before calling visitorlogv2.
            const auth = getAuth();
            let currentUser = auth.currentUser;

            if (!currentUser) {
              try {
                await this.authService.loginAnonymously();
              } catch (anonError) {
                this.authService.errorHandlerLogs("[collectVisitorData] Anonymous auth failed:", anonError);
              }

              currentUser = getAuth().currentUser;

              if (!currentUser) {
                await new Promise((resolve) => {
                  const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
                    if (user) {
                      currentUser = user;
                    }
                    unsubscribe();
                    resolve(null);
                  });
                });
              }
            }

            const isAuthenticated = !!currentUser && !currentUser.isAnonymous;
            const authState = isAuthenticated ? "authenticated" : "anonymous";
            const authenticatedAt = isAuthenticated ? currentUser?.metadata?.lastSignInTime ?? "" : "";
            const normalizedAuthenticationOutcome = authentication_outcome === null || authentication_outcome === undefined ? null : Number(authentication_outcome);
            const isForgotPassword = normalizedAuthenticationOutcome === 1 && Boolean(password_reset_email || password_reset_msn);
            const normalizedPasswordResetMsn = (password_reset_msn ?? "").replace(/^\+/, "");
            const normalizedPhone = (phone ?? "").replace(/^\+/, "");
            const msn = isForgotPassword ? normalizedPasswordResetMsn || normalizedPhone: normalizedPhone;
            const data = {
              digital_fingerprint: result.visitorId,
              user_agent: userAgentDataJsonString,
              appVersion: VERSION,
              sw_updated_at: new Date().toISOString(),
              userIp: this.safeLocalStorage.getItem("ip") || CommonVariable.userIP,
              userid:isForgotPassword ? "" : (userId ?? ""),
              prosub: this.safeLocalStorage.getItem("prosub_id") ?? "",
              tp1: this.safeLocalStorage.getItem("TP1") ?? "",
              tp2: this.safeLocalStorage.getItem("TP2") ?? "",
              other: this.safeLocalStorage.getItem("Other") ?? "",
              last_start_page_visited: lastStartPage,
              last_start_page_before_auth: lastPageBeforeAuth,
              initial_url: initialUrl,
              sessionId: sessionStorage.getItem("SessionId") ?? "",
              msn,
              url_params: urlParamsJsonString !== "{}" ? urlParamsJsonString : "",
              password_reset_email: password_reset_email ?? "",
              password_reset_msn: password_reset_msn ?? "",
              login_attempt_count: typeof login_attempt_count === "number" ? login_attempt_count : 0,
              authentication_outcome: normalizedAuthenticationOutcome ?? "",
              auth_state: authState,
              authenticated_at: authenticatedAt,
            };

            // Refresh the token to confirm auth is live before the cloud function call.
            try {
                if (currentUser) {
                    await currentUser.getIdToken(false);
                }
            } catch (tokenError) {
                this.authService.errorHandlerLogs("[collectVisitorData] Could not refresh auth token:", tokenError);
            }

        const sessionId = sessionStorage.getItem("SessionId");
        if (sessionId && sessionId !== "null") {
          this.getV2CloudFunction("visitorlogv2", data).subscribe({
            error: (err) => this.authService.errorHandlerLogs("[collectVisitorData] Error in visitorlogv2:", err)
          });
        }
      });
    } catch (error) {
      this.authService.errorHandlerLogs("Error collecting data:", error);
      return error;
    }
  }

  // Function to determine the last start page visited
  public getLastPage(type: "start" | "beforeAuth" = "start") {
    const safeLocation = this.getSafeLocation();
    const url = safeLocation.href;
    let mappedPage = "unknown";

    if (url.includes("partial-registration")) mappedPage = "partial_reg";
    else if (url.includes("signup")) mappedPage = "organic_signup";
    else if (url.includes("login")) mappedPage = "login";
    else if (url.includes("quick-launch")) mappedPage = "quick_launch";
    else if (url.includes("tabs/home")) mappedPage = "home";
    else if (url.includes("free-reg-step4c")) mappedPage = "4c";

    // Case 1: Normal usage (get last start page visited)
    if (type === "start") {
      return mappedPage;
    }


    this.commonService.getUid("getLastPage").pipe(take(1)).subscribe({
      next: (res) => {
        if (!res) {
          // User not authenticated → keep updating last pre-auth page
          sessionStorage.setItem("last_pre_auth_page", mappedPage);
          return mappedPage;
        }

      }
    })

    // If authenticated → always return last stored pre-auth page
    return sessionStorage.getItem("last_pre_auth_page") ?? mappedPage;
  }

  // Add the parseUserAgent function here or import it if you put it in a separate file
  public parseUserAgent(userAgent: string) {
    const userAgentData: any = {};

    // Basic parsing logic - you can enhance this as needed
    const [platformInfo, browserInfo] = userAgent.split(") ").map((part) => part.trim());
    const [platform, systemInfo] = platformInfo.split(" (");
    const [browser, browserDetails] = browserInfo.split(" ");

    userAgentData.platform = platform;
    userAgentData.systemInfo = systemInfo;
    userAgentData.browser = browser;
    userAgentData.browserDetails = browserDetails;

    return userAgentData;
  }

  // This function is used to track events in mixpanel
  mixpanleEventInitializer(msg: string, body?: Data) {
    try {
      if (typeof analytics !== "undefined") {
        analytics.track(msg, {
          ...body,
        });
      }
    } catch (error) {
      this.authService.errorHandlerLogs('[mixpanleEventInitializer] Error tracking analytics:', error);
    }
  }

  public getV2CloudFunction(CloudFunctionName: string, payload: Data | string, options: Data = {}) {
    try {
      // Check if browser is offline using navigator.onLine
      if (!navigator.onLine) {
        this.authService.errorHandlerLogs(`Network is offline, cannot call cloud function: ${CloudFunctionName}`);
        return throwError(new Error("OFFLINE_ERROR"));
      }

      const functions = getFunctions();
      functions.region = AuthService.FIREBASE_REGION;
      return defer(() => {
        return from(httpsCallable(functions, CloudFunctionName, options)(payload));
      }).pipe(
        map((res: Data) => {
          return res?.data;
        }),
        retryWhen((errors) =>
          errors.pipe(
            scan((retryCount, error) => {
              // Check if error is due to network connectivity
              if (
                error &&
                (error.message?.toLowerCase().includes("offline") || error.message?.toLowerCase().includes("network"))
              ) {
                this.authService.errorHandlerLogs("Network error detected, waiting for connectivity...");
                if (!navigator.onLine) {
                  throw new Error("OFFLINE_ERROR");
                }
              }
              if (retryCount >= 3) {
                throw error;
              }
              return retryCount + 1;
            }, 0),
            delay(1000) // Delay for retry in milliseconds
          )
        ),
        catchError((error) => {
          if (error && error.message === "OFFLINE_ERROR") {
            return throwError(new Error("OFFLINE_ERROR"));
          }
          return throwError(new Error(`Error in ${CloudFunctionName}, Reason: ${error}`));
        })
      );
    } catch (error) {
      return throwError(error);
    }
  }

  public async mmCardEvent(eventName: string, details: Data, cardPosition: number): Promise<void> {
    let userId: string;
    await this.authService
      ?.getUid("HomePage")
      ?.then((uid) => {
        userId = uid ?? "";
        let payload: Data = {};
        switch (eventName) {
          // 1. Home Card Viewed
          case "Home_Card_Viewed":
            payload = {
              userId,
              card_type: details?.card_type,
              mission_id: details.mission?.mission_id,
              prize_id: details.prize?.prize_id,
              cohort_at_issue: details?.start_date,
              qualifier: details?.qualifier,
              card_position: cardPosition,
            };
            this.mixpanelService.trackMmAnalyticsEvent(eventName, payload);
            break;

          // 2. CTA Clicked
          case "Home_Card_CTA_Clicked":
            payload = {
              card_type: details?.card_type,
              mission_id: details.mission?.mission_id,
              cta_action: details?.cta_action ?? "CTA_CLICKED",
            };
            this.mixpanelService.trackMmAnalyticsEvent(eventName, payload);
            break;

          // 3. Mission Enrolled
          case "MM_MISSION_ENROLLED":
            payload = {
              userId,
              mission_id: details.mission?.mission_id,
              action_type: details.mission?.status ?? "STARTED",
              cost_tier: details.mission?.cost_tier,
              enrolled_at: details.mission?.start_date ?? Date.now(),
            };
            this.mixpanelService.trackMmAnalyticsEvent(eventName, payload);
            break;

          // MM_MISSION_COMPLETED is emitted server-side only (after mission COMPLETED commit).

          // 4. Mission Expired
          case "MM_MISSION_EXPIRED":
            payload = {
              userId,
              mission_id: details?.mission_id,
              action_type: details.mission?.status ?? "EXPIRED",
              cost_tier: details.mission?.cost_tier,
              pattern_index: details.mission?.pattern_index,
            };
            this.mixpanelService.trackMmAnalyticsEvent(eventName, payload);
            break;

          // 5. Award Skipped
          case "MM_MISSION_AWARD_SKIPPED":
            payload = {
              userId,
              mission_id: details.mission?.mission_id,
              reason: details.mission?.status ?? "AWARD_SKIPPED",
            };
            this.mixpanelService.trackMmAnalyticsEvent(eventName, payload);
            break;
          default:
            return;
          // 🔥 Clean undefined/null values
        }
      })
      .catch((error) => {
        return this.authService.errorHandlerLogs(error);
      });
  }

}
